// src/pages/Login.js
// Apple Dark Mode Style with pendingGroup handling
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Auth.css';
import API_URL from '../config';

function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingGroup, setPendingGroup] = useState(null);

  // Check for pending group from wizard
  useEffect(() => {
    const pending = localStorage.getItem('pendingGroup');
    if (pending) {
      try {
        setPendingGroup(JSON.parse(pending));
      } catch (e) {
        localStorage.removeItem('pendingGroup');
      }
    }
  }, []);

  // Create pending group after successful login
  const createPendingGroup = async (token) => {
    if (!pendingGroup) return null;

    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      const response = await fetch(`${API_URL}/api/groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          groupName: pendingGroup.groupName,
          meetingFrequency: { 
            dayOfWeek: pendingGroup.meetingDay, 
            time: pendingGroup.meetingTime 
          },
          frequency: pendingGroup.frequency === 'weekly' ? 'Varje vecka' : 'Varannan vecka',
          participants: pendingGroup.emails.map(email => ({ 
            email, 
            name: email.split('@')[0] 
          })),
          threshold: Math.ceil(pendingGroup.emails.length * 0.5) || 1,
          timezone: userTimezone,
          location: '',
          invitationAdvance: 7,
          reminderAdvance: 3,
          decisionTime: '18:00'
        })
      });

      if (response.ok) {
        const newGroup = await response.json();
        localStorage.removeItem('pendingGroup');
        window.dispatchEvent(new Event('groupsUpdated'));
        return newGroup;
      }
    } catch (error) {
      console.error('Error creating pending group:', error);
    }
    
    localStorage.removeItem('pendingGroup');
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t('login.errors.loginFailed'));
      }

      // Save token and user data
      localStorage.setItem('token', data.token);
      localStorage.setItem('userEmail', data.user.email);
      localStorage.setItem('userId', data.user.id);
      localStorage.setItem('userPlan', data.user.accountType);
      
      // Check for pending group from wizard
      const createdGroup = await createPendingGroup(data.token);
      
      if (createdGroup) {
        // Redirect to the newly created group
        navigate(`/groups/${createdGroup._id}`);
      } else {
        // Redirect to dashboard
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>{t('login.title')}</h1>
        <p className="auth-subtitle">
          {t('login.subtitle')}
        </p>

        {/* Pending Group Notice */}
        {pendingGroup && (
          <div className="pending-group-notice">
            <p>
              {t('login.pendingGroup')} <strong>"{pendingGroup.groupName}"</strong> {t('login.pendingGroupWaiting')}
            </p>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">{t('login.emailLabel')}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t('login.emailPlaceholder')}
              autoComplete="email"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('login.passwordLabel')}</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder={t('login.passwordPlaceholder')}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button 
            type="submit" 
            className={`auth-button ${loading ? 'loading' : ''}`}
            disabled={loading}
          >
            {loading ? t('login.loggingIn') : t('login.submitButton')}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            {t('login.noAccount')}{' '}
            <Link to="/signup">{t('login.signupLink')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
